import { useState, useEffect } from "react";
import { QrCode, Download, Loader2 } from "lucide-react";
import { Modal, Spinner } from "../ui/index.jsx";
import {
  loadFonts,
  renderHorizontalLabel,
  renderVerticalLabel,
} from "../../utils/labelRenderer";
import apiClient from "../../api/client";
import { ProvisionalNumberWarning } from "./ProvisionalNumberWarning.jsx";

// ---------------------------------------------------------------------------
// Fetch label data from API
// ---------------------------------------------------------------------------
function useLabelData(assetId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!assetId) return;
    setLoading(true);
    apiClient
      .get(`/api/labels/${assetId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [assetId]);

  return { data, loading, error };
}

// ---------------------------------------------------------------------------
// Render a single label format
// ---------------------------------------------------------------------------
function useLabelImage(labelData, format) {
  const [imgUrl, setImgUrl] = useState(null);
  const [rendering, setRendering] = useState(false);

  const key = labelData
    ? `${labelData.assetId}-${labelData.assetNumber}-${labelData.assetName}-${format}`
    : null;

  useEffect(() => {
    if (!labelData?.qrDataUrl) return;
    setRendering(true);
    setImgUrl(null);
    loadFonts()
      .then(() =>
        format === "horizontal"
          ? renderHorizontalLabel(labelData)
          : renderVerticalLabel(labelData),
      )
      .then(setImgUrl)
      .catch(console.error)
      .finally(() => setRendering(false));
  }, [key]);

  return { imgUrl, rendering };
}

// ---------------------------------------------------------------------------
// Single format preview panel inside the modal
// ---------------------------------------------------------------------------
function FormatPanel({ labelData, format }) {
  const { imgUrl, rendering } = useLabelImage(labelData, format);
  const isH = format === "horizontal";

  function handleDownload() {
    if (!imgUrl) return;
    const a = document.createElement("a");
    a.href = imgUrl;
    a.download = `${labelData.assetNumber}-${isH ? "75x20" : "15x20"}.jpg`;
    a.click();
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
        {isH
          ? "Horizontal — 75×20mm"
          : "Vertical — 20×30mm outer / 15×20mm inner"}
      </p>

      {/* Rendered label at display size */}
      <div
        className="border border-slate-300 rounded overflow-hidden shadow-sm bg-slate-50 flex items-center justify-center"
        style={isH ? { width: 300, height: 80 } : { width: 100, height: 150 }}
      >
        {rendering ? (
          <Spinner />
        ) : imgUrl ? (
          <img
            src={imgUrl}
            alt={`${format} label`}
            className="w-full h-full object-fill"
          />
        ) : (
          <QrCode className="h-8 w-8 text-slate-300" />
        )}
      </div>

      <p className="text-xs text-slate-400">
        {isH ? "75 × 20 mm" : "20 × 30 mm outer · 15 × 20 mm inner sticker"}
      </p>

      {imgUrl && (
        <button
          type="button"
          onClick={handleDownload}
          className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline"
        >
          <Download className="h-3.5 w-3.5" /> Download JPEG
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// QrLabelModal — shows both formats side by side
// ---------------------------------------------------------------------------
export function QrLabelModal({ assetId, labelDataOverride, isOpen, onClose }) {
  const { data: fetched, loading } = useLabelData(isOpen ? assetId : null);

  // If override provided but missing qrDataUrl (live form preview case),
  // take the qrDataUrl from the API response and merge with override fields
  let labelData = labelDataOverride || fetched;
  if (labelDataOverride && !labelDataOverride.qrDataUrl && fetched?.qrDataUrl) {
    labelData = { ...labelDataOverride, qrDataUrl: fetched.qrDataUrl };
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="QR Label Preview" size="lg">
      <ProvisionalNumberWarning className="mb-4" />
      {loading && (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      )}

      {!loading && labelData && (
        <div className="flex flex-col sm:flex-row gap-8 justify-center py-2">
          <FormatPanel labelData={labelData} format="horizontal" />
          <div className="hidden sm:block w-px bg-slate-200" />
          <FormatPanel labelData={labelData} format="vertical" />
        </div>
      )}

      {!loading && !labelData && (
        <div className="text-center py-12">
          <QrCode className="h-10 w-10 text-slate-200 mx-auto mb-3" />
          <p className="text-sm text-slate-400">No label data available yet.</p>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// QrPreviewButton — single button, opens QrLabelModal
// IMPORTANT: always type="button" so it never submits a parent form
// ---------------------------------------------------------------------------
export function QrPreviewButton({ assetId, labelDataOverride, className }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-primary-300 hover:text-primary-600 transition-colors ${className || ""}`}
      >
        <QrCode className="h-4 w-4" />
        Preview QR Label
      </button>

      <QrLabelModal
        assetId={assetId}
        labelDataOverride={labelDataOverride}
        isOpen={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
